import { useEffect, useState } from "react";
import { api, formatApiErrorDetail, fmtMoney } from "@/lib/api";
import { PageHeader, Section, Pill } from "@/components/ui-kit";
import { toast } from "sonner";

export default function PlatformSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reveal, setReveal] = useState(false);

  const load = () => api.get("/admin/settings").then(({ data }) => setS(data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings", s);
      setS(data);
      toast.success("Platform settings saved.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  if (!s) return <div className="label">Loading…</div>;

  const inputCls = "w-full bg-white border border-border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm";

  return (
    <div data-testid="platform-settings-page">
      <PageHeader
        title="Platform Settings"
        subtitle="Global Admin · Stripe & SendGrid keys"
      />

      <form onSubmit={save}>
        <Section title="Branding" testId="section-branding">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="label block mb-2">Platform Name</label>
              <input className={inputCls} value={s.platform_name || ""}
                     onChange={(e) => setS({ ...s, platform_name: e.target.value })}
                     data-testid="settings-platform-name" />
            </div>
            <div>
              <label className="label block mb-2">Logo URL (optional)</label>
              <input className={inputCls} value={s.platform_logo_url || ""}
                     onChange={(e) => setS({ ...s, platform_logo_url: e.target.value })}
                     data-testid="settings-platform-logo" />
            </div>
          </div>
        </Section>

        <Section title="Stripe (Subscription Payments)" testId="section-stripe"
          actions={<button type="button" onClick={() => setReveal(!reveal)} className="btn-ghost" data-testid="toggle-reveal-keys">{reveal ? "Hide" : "Reveal"} keys</button>}>
          <p className="text-sm text-muted-foreground mb-4">
            Configured at the global admin level. These keys are used by all tenants to process subscription checkout. Get them from your{" "}
            <a className="text-forest underline" href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noreferrer">Stripe dashboard</a>.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="label block mb-2">Publishable Key</label>
              <input className={inputCls} type={reveal ? "text" : "password"} value={s.stripe_publishable_key || ""}
                     onChange={(e) => setS({ ...s, stripe_publishable_key: e.target.value })}
                     data-testid="settings-stripe-publishable" />
            </div>
            <div>
              <label className="label block mb-2">Secret Key</label>
              <input className={inputCls} type={reveal ? "text" : "password"} value={s.stripe_secret_key || ""}
                     onChange={(e) => setS({ ...s, stripe_secret_key: e.target.value })}
                     data-testid="settings-stripe-secret" />
            </div>
            <div>
              <label className="label block mb-2">Webhook Signing Secret</label>
              <input className={inputCls} type={reveal ? "text" : "password"} value={s.stripe_webhook_secret || ""}
                     onChange={(e) => setS({ ...s, stripe_webhook_secret: e.target.value })}
                     data-testid="settings-stripe-webhook" />
            </div>
            <div>
              <label className="label block mb-2">Default Currency</label>
              <select className={inputCls} value={s.stripe_currency || "USD"}
                     onChange={(e) => setS({ ...s, stripe_currency: e.target.value })}
                     data-testid="settings-stripe-currency">
                <option value="USD">USD — US Dollar</option>
                <option value="NGN">NGN — Nigerian Naira</option>
                <option value="EUR">EUR — Euro</option>
                <option value="GBP">GBP — British Pound</option>
                <option value="GHS">GHS — Ghanaian Cedi</option>
                <option value="KES">KES — Kenyan Shilling</option>
                <option value="ZAR">ZAR — South African Rand</option>
              </select>
            </div>
          </div>
          <div className="text-xs text-muted-foreground mt-4">
            Webhook URL to register in Stripe: <code className="bg-muted px-2 py-0.5 rounded">{`${window.location.origin}/api/webhook/stripe`}</code>
          </div>
        </Section>

        <Section title="SendGrid (Email Notifications)" testId="section-sendgrid">
          <p className="text-sm text-muted-foreground mb-4">
            Optional. When configured, the platform will send transactional emails via SendGrid.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="label block mb-2">API Key</label>
              <input className={inputCls} type={reveal ? "text" : "password"} value={s.sendgrid_api_key || ""}
                     onChange={(e) => setS({ ...s, sendgrid_api_key: e.target.value })}
                     data-testid="settings-sendgrid-api-key" />
            </div>
            <div>
              <label className="label block mb-2">From Email</label>
              <input className={inputCls} value={s.sendgrid_from_email || ""}
                     onChange={(e) => setS({ ...s, sendgrid_from_email: e.target.value })}
                     data-testid="settings-sendgrid-from-email" />
            </div>
            <div>
              <label className="label block mb-2">From Name</label>
              <input className={inputCls} value={s.sendgrid_from_name || ""}
                     onChange={(e) => setS({ ...s, sendgrid_from_name: e.target.value })}
                     data-testid="settings-sendgrid-from-name" />
            </div>
          </div>
        </Section>

        <div className="flex items-center justify-end gap-3 mt-6">
          <Pill variant="info">Saved at: {new Date(s.updated_at).toLocaleString()}</Pill>
          <button type="submit" disabled={saving} className="btn-primary" data-testid="settings-save-btn">
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}
